import React, { useEffect, useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import { Divider, PageContainer, PageControlSkeleton, Poppins, Spacer } from 'src/common';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { queryCache, useQuery } from 'react-query';
import { ConfirmDeleteModal, RiskManagementModal } from 'src/components/modals';
import { ControllStatus, QKeys } from 'src/api/types';
import { getControl } from 'src/api/controls';
import { MetaSheet } from './comps';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import { Alert } from '@mui/material';
import useVersion from 'src/hooks/useVersion';
import { AuditSection, LinkedScenariosSection } from './sections';
import { ObjectKind, useControlQuery, useDeleteControlsMutation } from 'src/graphql/_generated';
import { isQueryLoading, useCache } from 'src/graphql';
import { useApolloClient } from '@apollo/client';
import { showSuccessToast, toastMessages } from 'src/components/Toast';
import { FrameworksSection } from './sections/FrameworksSection';
import ActionsSection from 'src/components/ActionsSection';

const ControlPage = () => {
  const { controlId } = useParams<{ controlId: string }>();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { setLocationPaths } = useActionsApp();
  const { versionId } = useVersion();
  useGaEventPageView('Cydea | Risk Management Plan | Control');
  const { refetchQuery } = useCache();
  const client = useApolloClient();

  const navigate = useNavigate();

  const {
    data: { control } = {},
    loading,
    networkStatus,
    error,
  } = useControlQuery({ variables: { id: controlId!, versionId } });
  const isLoading = isQueryLoading({ data: control, loading, networkStatus });

  useQuery([QKeys.Control, controlId, { version: versionId }], () =>
    getControl(controlId as string, { version: versionId }),
  );

  const riskManagementPlansCount = control?.riskManagementPlans.length ?? 0;

  useEffect(() => {
    if (control) {
      setLocationPaths({ rmpControl: { id: control.id, name: control.name } });
    }
    return () => {
      setLocationPaths({ rmpControl: undefined });
    };
  }, [control]);

  const [remove] = useDeleteControlsMutation({ variables: { ids: [controlId!] } });

  return (
    <>
      <Meta title={`Cydea | Risk Management Plan | Control`} feedbackScript />

      {isDeleteModalOpen && (
        <ConfirmDeleteModal
          onConfirm={async () => {
            await remove();
            client.cache.evict({ id: client.cache.identify({ __typename: 'Control', id: controlId }) });
            client.cache.gc();
            queryCache.invalidateQueries([QKeys.Audit]);
            refetchQuery({ include: ['controls'] });
            showSuccessToast(toastMessages.control.deleted);
            navigate('/risk-management');
          }}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          {riskManagementPlansCount > 0 && (
            <Alert severity="warning">
              {riskManagementPlansCount > 1
                ? 'This control is linked to scenarios.'
                : 'This control is linked to a scenario.'}
            </Alert>
          )}
          {control?.status === ControllStatus.Live && <Alert severity="warning">This control is already live.</Alert>}
        </ConfirmDeleteModal>
      )}
      {isEditModalOpen && (
        <RiskManagementModal
          onClose={() => setIsEditModalOpen(false)}
          controlId={controlId}
          invalidateQueries={['control']}
        />
      )}

      <PageContainer data-cy="control-page">
        {error && <Poppins color="error">{error.message}</Poppins>}
        {isLoading && <PageControlSkeleton />}
        {control && (
          <>
            <MetaSheet
              data={control}
              editable={{
                onDeleteSelect: () => setIsDeleteModalOpen(true),
                onEditSelect: () => setIsEditModalOpen(true),
              }}
            >
              <Spacer $px={20} />
              <Divider />
              <FrameworksSection control={control} />
              <Divider />
              <LinkedScenariosSection version={versionId} control={control} />
              <Divider />
              <ActionsSection objectType={ObjectKind.Control} objectId={control.id} name={control.name} />
              <Divider />
              <AuditSection version={versionId} />
            </MetaSheet>
          </>
        )}
      </PageContainer>
    </>
  );
};

export default ControlPage;
